import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import {
  $composeConvertFromMarkdownString,
  $composeConvertToMarkdownString,
} from "@mirage/mosaics/ComposeAssistant/components/editor/ComposeMarkdownTransformers";
import { tagged } from "@mirage/service-logging";
import { debounce } from "lodash";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";

import type { EditorState } from "lexical";

const logger = tagged("ComposeContentUpdaterPlugin");

/**
 * This plugin is responsible for propagating changes down to the editor when markdownContent changes,
 * as well as to manage changes from the editor (i.e. user edits).
 */
interface ComposeContentUpdaterPluginProps {
  supportsTables: boolean;
  markdownContent: string;
  onChangeContent: (markdownContent: string) => void;
  isStreaming: boolean;
}
export const ComposeContentUpdaterPlugin = memo(
  ({
    supportsTables,
    markdownContent,
    onChangeContent,
    isStreaming = false,
  }: ComposeContentUpdaterPluginProps) => {
    const [editor] = useLexicalComposerContext();
    const markdownContentRef = useRef("");
    const renderedMarkdownContentRef = useRef("");
    const isPropagatingUpdateRef = useRef(false);
    // Create a debounced version of onChangeContent that also
    // manages the userEditing state
    const userEditingRef = useRef(false);
    const scrollPositionRef = useRef<{ x: number; y: number } | null>(null);

    const debouncedOnChangeContent = useMemo(
      () =>
        debounce((mdText: string) => {
          onChangeContent(mdText);
          // After update, keep userEditing true for a short time to prevent
          // cursor jumps from immediate updates
          setTimeout(() => {
            userEditingRef.current = false;
          }, 200);
        }, 150),
      [onChangeContent],
    );

    useEffect(() => {
      if (markdownContentRef.current === markdownContent) {
        return; // only update if markdownContent prop has changed
      }
      if (renderedMarkdownContentRef.current === markdownContent) {
        return; // ignore updates that are already reflected in the editor
      }

      // Skip updates while user is actively editing to prevent cursor jumps
      if (userEditingRef.current && !isStreaming) {
        logger.debug("Skipping update while user is editing");
        return;
      }

      markdownContentRef.current = markdownContent;
      logger.log("updating editor content", markdownContent);

      // If streaming, save current scroll position before update
      if (isStreaming) {
        scrollPositionRef.current = {
          x: window.scrollX,
          y: window.scrollY,
        };
      }

      isPropagatingUpdateRef.current = true;
      editor.update(
        () => {
          $composeConvertFromMarkdownString(markdownContent, supportsTables);

          // Prevent cursor from being set during streaming
          if (isStreaming) {
            // Clear any selection to prevent cursor from pulling focus
            const selection = window.getSelection();
            if (selection) {
              selection.removeAllRanges();
            }
          }
        },
        {
          onUpdate: () => {
            editor.getEditorState().read(() => {
              isPropagatingUpdateRef.current = false;
              renderedMarkdownContentRef.current =
                $composeConvertToMarkdownString({
                  includeSpecialAttributes: true,
                });

              // Restore scroll position after update if streaming
              if (isStreaming && scrollPositionRef.current) {
                setTimeout(() => {
                  window.scrollTo(
                    scrollPositionRef.current?.x || 0,
                    scrollPositionRef.current?.y || 0,
                  );
                }, 0);
              }
            });
          },
        },
      );
    }, [editor, markdownContent, supportsTables, isStreaming]);

    // Make editor read-only during streaming to prevent cursor interactions
    useEffect(() => {
      if (isStreaming) {
        editor.setEditable(false);
      } else {
        editor.setEditable(true);
      }
    }, [editor, isStreaming]);

    const handleChange = useCallback(
      (editorState: EditorState) => {
        editorState.read(() => {
          if (isPropagatingUpdateRef.current) {
            return; // ignore changes from the editor while we're propagating updates down
          }
          const mdText = $composeConvertToMarkdownString({
            includeSpecialAttributes: true,
          });
          if (mdText === renderedMarkdownContentRef.current) {
            return; // ignore changes that don't actually change the content
          }
          userEditingRef.current = true;
          renderedMarkdownContentRef.current = mdText;
          debouncedOnChangeContent(mdText);
        });
      },
      [debouncedOnChangeContent],
    );
    return <OnChangePlugin onChange={handleChange} ignoreSelectionChange />;
  },
);
ComposeContentUpdaterPlugin.displayName = "ComposeContentUpdaterPlugin";
